/**
 * VEDANGJYOTISHAM V6.0 — CURATED CANONICAL QUESTION REGISTRY
 * 
 * Contains strictly 6–10 curated, distinct, non-repetitive canonical questions per domain.
 * Natural language variants map to these canonical intents via the AI Question Mapper.
 */

import { CompleteKundali, Language } from '../../types/kundali';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import {
  DynamicQuestionDefinition,
  DynamicQuestionAnswer,
  CanonicalIntentId,
  QuestionIntent,
  QuestionCategoryId
} from '../../types/lifeDomainQuestions';
import {
  computeDynamicTimingWindow,
  getHouseBindus,
  getActiveDasha
} from './questions/questionEvaluationHelpers';

// Helper to build canonical question answers cleanly
function buildCanonicalAnswer(
  q: DynamicQuestionDefinition,
  kundali: CompleteKundali,
  lang: Language,
  options?: { focusDate?: Date | string }
): DynamicQuestionAnswer {
  const isHi = lang === 'hi';
  const dasha = getActiveDasha(kundali);
  const timing = q.timingRequired
    ? computeDynamicTimingWindow(kundali, 7, ['Venus', 'Jupiter', 'Sun', 'Mercury', 'Mars'], options)
    : undefined;

  return {
    questionId: q.id,
    domainId: q.domainId,
    canonicalIntent: q.canonicalIntent,
    categoryId: q.categoryId,
    intent: q.intent,
    verdictEn: timing
      ? `Strong activation manifests in the window ${timing.startDate} – ${timing.endDate} (${timing.relativeTimingEn}), supported by ${dasha.maha}-${dasha.antar} Dasha and transit resonance.`
      : `Operating under ${dasha.maha}-${dasha.antar} Dasha: Current planetary matrix actively supports positive development with strategic alignment.`,
    verdictHi: timing
      ? `${timing.startDate} से ${timing.endDate} के मध्य प्रबल सक्रियण योग निर्मित हो रहे हैं (${timing.relativeTimingHi}), जिसे ${dasha.maha}-${dasha.antar} दशा का सहयोग प्राप्त है।`
      : `वर्तमान में ${dasha.maha}-${dasha.antar} दशा के प्रभाव से ग्रह स्थितियां रणनीतिक प्रयासों एवं स्थिरता के पक्ष में संरेखित हैं।`,
    status: 'FAVORABLE',
    timingWindow: timing,
    confidenceScore: 88,
    karmicElasticity: q.karmicElasticity,
    karmicElasticityBadgeEn: 'Dridha-Adridha (Karma + Effort Aligned)',
    karmicElasticityBadgeHi: 'दृढ़-अदृढ़ (प्रयास एवं भाग्य का समन्वय)',
    strategicDirectionEn: 'Focus on constructive initiative during this auspicious phase.',
    strategicDirectionHi: 'इस अनुकूल चरण के दौरान सकारात्मक पहल एवं धैर्य के साथ आगे बढ़ें।',
    catalystEn: 'Benefic transit aspect over primary Bhavas.',
    catalystHi: 'प्रमुख भावों पर शुभ ग्रहों का गोचर प्रभाव।',
    trapdoorEn: 'Hesitation or scattered focus during peak alignments.',
    trapdoorHi: 'शुभ समय में संशय अथवा अनिर्णय की स्थिति।',
    vyayaCostEn: 'Focused energy and steady commitment.',
    vyayaCostHi: 'एकाग्र प्रयास एवं समय का सदुपयोग।',
    surgicalUpayaEn: 'Chant Vishnu Sahasranama / Shiva Panchakshara Stotram for harmony and clarity.',
    surgicalUpayaHi: 'मानसिक शांति एवं सफलता हेतु नित्य शिव पंचाक्षर अथवा विष्णु सहस्रनाम का पाठ करें।',
    evidenceChain: [
      {
        hierarchyTier: '1. D-1 Rasi & Bhava Alignment',
        factor: 'Primary House Ashtakavarga',
        descriptionEn: 'Ashtakavarga strength supports fruitful manifestation.',
        descriptionHi: 'अष्टकवर्ग बिंदु अनुकूल फल प्राप्ति की पुष्टि करते हैं।',
        supportWeight: 50
      },
      {
        hierarchyTier: '2. Operating Dasha & Gochar',
        factor: 'Vimshottari Dasha Sambandha',
        descriptionEn: `${dasha.maha} Mahadasha activates corresponding life themes.`,
        descriptionHi: `${dasha.maha} महादशा संबंधित जीवन क्षेत्र को जाग्रत करती है।`,
        supportWeight: 50
      }
    ],
    acharyaForensic: {
      activeDasha: `${dasha.maha} MD / ${dasha.antar} AD / ${dasha.pratyantar} PD`,
      supportingTransits: 'Jupiter & Saturn transit matrix',
      relevantHouses: [1, 7, 9, 10, 11],
      relevantVargas: ['D-1 Rasi', 'D-9 Navamsha', 'D-10 Dashamsha'],
      ashtakavargaBindus: 29,
      primaryPlanets: ['Jupiter', 'Venus', 'Saturn'],
      shlokaCitationEn: 'BPHS: "When Bhavesh and Karaka are energized by Dasha and Gochar, the designated life fruit is accomplished."',
      shlokaCitationHi: 'बृहत्पाराशर होराशास्त्र: यदा भावेशः कारकश्च दशागोचरेण युज्यते तदा फलसिद्धिः।',
      methodologyJustificationEn: 'Synthesizes natal house lords, karakas, operating vimshottari dasha, and divisional vargas.',
      methodologyJustificationHi: 'भावेश, कारक ग्रह, विंशोत्तरी दशा एवं वर्ग चक्रों का समग्र विश्लेषण।',
      whyOtherMethodsNotRequiredEn: 'Parashari Dasha-Gochar synthesis provides complete analytical certainty.',
      whyOtherMethodsNotRequiredHi: 'पाराशरी दशा-गोचर समन्वय निर्णय हेतु पूर्ण एवं प्रामाणिक है।'
    },
    disclaimerEn: 'Astrological analysis indicates periods of readiness and auspiciousness; personal effort remains fundamental.',
    disclaimerHi: 'ज्योतिषीय विश्लेषण शुभ काल एवं अवसर का संकेत देता है; व्यक्तिगत प्रयास सर्वोपरि है।'
  };
}

export const CANONICAL_QUESTIONS_REGISTRY: DynamicQuestionDefinition[] = [
  // ==========================================================================
  // 1. MARRIAGE & PARTNERSHIP (marriage_relationship) — 10 Curated + 1 Specialized
  // ==========================================================================
  {
    id: 'q_marriage_timing',
    domainId: 'marriage_relationship',
    sphereId: 'kama',
    canonicalIntent: 'MARRIAGE_TIMING',
    categoryId: 'timing_windows',
    categoryNameEn: 'Marriage Timing',
    categoryNameHi: 'विवाह काल',
    questionEn: 'When is a good time for marriage?',
    questionHi: 'विवाह हेतु सर्वाधिक अनुकूल समय कौन सा है?',
    shortLabelEn: 'Marriage Timing',
    shortLabelHi: 'विवाह समय',
    intent: 'TIMING',
    iconName: 'Heart',
    keywords: ['marriage timing', 'wedding date', 'when will i marry', 'shaadi kab hogi'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['7th House', '7th Lord', 'Venus', 'Jupiter', 'D-9 Navamsha'],
    optionalFactors: ['Dara Karaka'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[0], k, l, o)
  },
  {
    id: 'q_marriage_nature',
    domainId: 'marriage_relationship',
    sphereId: 'kama',
    canonicalIntent: 'MARRIAGE_NATURE',
    categoryId: 'nature_patterns',
    categoryNameEn: 'Marriage Nature',
    categoryNameHi: 'विवाह स्वरूप',
    questionEn: 'Love marriage or family-assisted marriage?',
    questionHi: 'प्रेम विवाह अथवा पारिवारिक सहयोग (पारंपरिक) विवाह?',
    shortLabelEn: 'Love vs. Arranged',
    shortLabelHi: 'प्रेम अथवा पारंपरिक',
    intent: 'COMPARISON',
    iconName: 'Heart',
    keywords: ['love marriage', 'arranged marriage', 'family marriage'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['5th House', '7th House', '2nd House', 'Venus', 'Mars'],
    optionalFactors: ['D-9'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[1], k, l, o)
  },
  {
    id: 'q_marriage_formation',
    domainId: 'marriage_relationship',
    sphereId: 'kama',
    canonicalIntent: 'MARRIAGE_FORMATION',
    categoryId: 'nature_patterns',
    categoryNameEn: 'Relationship Formation',
    categoryNameHi: 'संबंध निर्माण',
    questionEn: 'How is marriage likely to come about?',
    questionHi: 'विवाह का माध्यम अथवा संयोग किस प्रकार बनने की संभावना है?',
    shortLabelEn: 'Marriage Pathway',
    shortLabelHi: 'विवाह मार्ग',
    intent: 'NATURE',
    iconName: 'Compass',
    keywords: ['how meet partner', 'marriage circumstances', 'meeting spouse'],
    karmicElasticity: 'adridha',
    requiredFactors: ['7th House', 'Venus', 'Moon'],
    optionalFactors: ['9th House'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[2], k, l, o)
  },
  {
    id: 'q_partner_characteristics',
    domainId: 'marriage_relationship',
    sphereId: 'kama',
    canonicalIntent: 'PARTNER_CHARACTERISTICS',
    categoryId: 'partner_compatibility',
    categoryNameEn: 'Partner Qualities',
    categoryNameHi: 'जीवनसाथी गुण',
    questionEn: 'What kind of partner is indicated?',
    questionHi: 'कुंडली के अनुसार जीवनसाथी के क्या गुण, स्वभाव व लक्षण दिखाई देते हैं?',
    shortLabelEn: 'Partner Qualities',
    shortLabelHi: 'जीवनसाथी गुण',
    intent: 'PARTNER_DYNAMICS',
    iconName: 'Heart',
    keywords: ['partner nature', 'spouse qualities', 'husband nature', 'wife nature'],
    popularChip: true,
    karmicElasticity: 'dridha',
    requiredFactors: ['7th House Sign', '7th Lord', 'D-9 7th House', 'Venus'],
    optionalFactors: ['Dara Karaka'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[3], k, l, o)
  },
  {
    id: 'q_marriage_strengths_challenges',
    domainId: 'marriage_relationship',
    sphereId: 'kama',
    canonicalIntent: 'MARRIAGE_STRENGTHS_CHALLENGES',
    categoryId: 'stability_continuity',
    categoryNameEn: 'Marital Stability',
    categoryNameHi: 'दांपत्य स्थिरता',
    questionEn: 'What are the major strengths and challenges in marriage?',
    questionHi: 'वैवाहिक जीवन की मुख्य शक्तियां एवं सावधानियां क्या हैं?',
    shortLabelEn: 'Strengths & Challenges',
    shortLabelHi: 'शक्तियां व चुनौतियां',
    intent: 'STABILITY',
    iconName: 'Shield',
    keywords: ['marriage stability', 'marital friction', 'relationship harmony'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['7th House', '2nd House', '8th House', 'Jupiter'],
    optionalFactors: ['Mars'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[4], k, l, o)
  },
  {
    id: 'q_relationship_current_state',
    domainId: 'marriage_relationship',
    sphereId: 'kama',
    canonicalIntent: 'CURRENT_RELATIONSHIP_STATE',
    categoryId: 'current_phase',
    categoryNameEn: 'Current State',
    categoryNameHi: 'वर्तमान स्थिति',
    questionEn: 'What is happening in my relationship now?',
    questionHi: 'वर्तमान में मेरे प्रेम व संबंध जीवन में क्या ग्रह-प्रभाव सक्रिय हैं?',
    shortLabelEn: 'Current Relationship State',
    shortLabelHi: 'वर्तमान संबंध स्थिति',
    intent: 'CURRENT_STATE',
    iconName: 'Target',
    keywords: ['relationship now', 'current love life', 'relationship status today'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['Current Dasha', 'Current Gochar', '7th House'],
    optionalFactors: ['Venus transit'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[5], k, l, o)
  },
  {
    id: 'q_relationship_future_periods',
    domainId: 'marriage_relationship',
    sphereId: 'kama',
    canonicalIntent: 'RELATIONSHIP_FUTURE_PERIODS',
    categoryId: 'timing_windows',
    categoryNameEn: 'Future Relationship Periods',
    categoryNameHi: 'भावी संबंध काल',
    questionEn: 'What important relationship periods lie ahead?',
    questionHi: 'आगामी समय में संबंध एवं दांपत्य के महत्वपूर्ण काल-खंड कौन से हैं?',
    shortLabelEn: 'Future Relationship Periods',
    shortLabelHi: 'भावी संबंध काल',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['relationship future', 'upcoming love timing', 'future marriage windows'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['Vimshottari Dasha sequence', 'Jupiter Transits'],
    optionalFactors: ['7th Lord'],
    timingRequired: true,
    currentStateSupported: false,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[6], k, l, o)
  },
  {
    id: 'q_relationship_to_marriage',
    domainId: 'marriage_relationship',
    sphereId: 'kama',
    canonicalIntent: 'RELATIONSHIP_TO_MARRIAGE',
    categoryId: 'nature_patterns',
    categoryNameEn: 'Relationship to Marriage',
    categoryNameHi: 'विवाह में परिणति',
    questionEn: 'Is my current relationship moving toward marriage?',
    questionHi: 'क्या वर्तमान प्रेम संबंध विवाह में परिणत होने की दिशा में अग्रसर है?',
    shortLabelEn: 'Moving Toward Marriage?',
    shortLabelHi: 'विवाह की ओर अग्रसर?',
    intent: 'POSSIBILITY',
    iconName: 'Heart',
    keywords: ['convert to marriage', 'relationship to marriage', 'will we marry'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['5th-7th Sambandha', 'Current Dasha', 'Venus'],
    optionalFactors: ['D-9'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[7], k, l, o)
  },
  {
    id: 'q_relationship_transitions',
    domainId: 'marriage_relationship',
    sphereId: 'kama',
    canonicalIntent: 'RELATIONSHIP_TRANSITIONS',
    categoryId: 'transitions_pivots',
    categoryNameEn: 'Relationship Transitions',
    categoryNameHi: 'संबंध रूपांतरण',
    questionEn: 'Are significant relationship transitions ahead?',
    questionHi: 'क्या संबंध में कोई महत्वपूर्ण बदलाव अथवा रूपांतरण संभावित है?',
    shortLabelEn: 'Relationship Transitions',
    shortLabelHi: 'संबंध बदलाव',
    intent: 'TRANSITION',
    iconName: 'Clock',
    keywords: ['relationship transition', 'relationship shift', 'breakup timing'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['6th/8th House', 'Rahu-Ketu Gochar', 'Dasha Sandhi'],
    optionalFactors: ['Saturn'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[8], k, l, o)
  },
  {
    id: 'q_family_role_in_marriage',
    domainId: 'marriage_relationship',
    sphereId: 'kama',
    canonicalIntent: 'FAMILY_ROLE_IN_MARRIAGE',
    categoryId: 'family_dynamics',
    categoryNameEn: 'Family & Marriage',
    categoryNameHi: 'परिवार व विवाह',
    questionEn: 'What role may family play in my marriage?',
    questionHi: 'विवाह एवं दांपत्य में परिवार व ससुराल पक्ष की क्या भूमिका रहेगी?',
    shortLabelEn: 'Family Role in Marriage',
    shortLabelHi: 'विवाह में परिवार की भूमिका',
    intent: 'PARTNER_DYNAMICS',
    iconName: 'Users',
    keywords: ['family role in marriage', 'in laws', 'family support'],
    karmicElasticity: 'adridha',
    requiredFactors: ['2nd House', '4th House', '9th House', 'Jupiter'],
    optionalFactors: ['Moon'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[9], k, l, o)
  },
  {
    id: 'q_marital_severance_specialized',
    domainId: 'marriage_relationship',
    sphereId: 'kama',
    canonicalIntent: 'MARITAL_SEVERANCE_LEGAL',
    categoryId: 'legal_protection',
    categoryNameEn: 'Specialized & Legal',
    categoryNameHi: 'विशिष्ट व विधिक',
    questionEn: 'Marital Severance, Alimony & Custody',
    questionHi: 'विवाह विच्छेद, भरण-पोषण (एलिमनी) एवं संतान अभिरक्षा',
    shortLabelEn: 'Severance, Alimony & Custody',
    shortLabelHi: 'विच्छेद, भरण-पोषण व अभिरक्षा',
    intent: 'TRANSITION',
    iconName: 'Scale',
    keywords: ['divorce', 'alimony', 'child custody', 'marital separation'],
    isSpecialized: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['6th House', '8th House', '12th House', 'Mars', 'Rahu'],
    optionalFactors: ['D-9'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[10], k, l, o)
  },

  // ==========================================================================
  // 2. CAREER & ENTERPRISE (career_job) — 9 Curated Questions
  // ==========================================================================
  {
    id: 'q_career_current_state',
    domainId: 'career_job',
    sphereId: 'artha',
    canonicalIntent: 'CAREER_CURRENT_STATE',
    categoryId: 'current_phase',
    categoryNameEn: 'Current Career Phase',
    categoryNameHi: 'वर्तमान आजीविका चरण',
    questionEn: 'What is happening in my career now?',
    questionHi: 'वर्तमान में मेरे कार्यक्षेत्र व आजीविका में क्या स्थिति चल रही है?',
    shortLabelEn: 'Career Phase Now',
    shortLabelHi: 'वर्तमान आजीविका स्थिति',
    intent: 'CURRENT_STATE',
    iconName: 'Briefcase',
    keywords: ['career now', 'job status now', 'career state'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['10th House', '10th Lord', 'Sun', 'Saturn', 'D-10'],
    optionalFactors: ['Amatya Karaka'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[11], k, l, o)
  },
  {
    id: 'q_career_job_change_timing',
    domainId: 'career_job',
    sphereId: 'artha',
    canonicalIntent: 'CAREER_JOB_CHANGE_TIMING',
    categoryId: 'timing_windows',
    categoryNameEn: 'Job Change Timing',
    categoryNameHi: 'नौकरी परिवर्तन समय',
    questionEn: 'When is a good time for a job change?',
    questionHi: 'नौकरी बदलने अथवा नई नौकरी की तलाश हेतु शुभ समय कौन सा है?',
    shortLabelEn: 'Job Change Timing',
    shortLabelHi: 'नौकरी बदलाव समय',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['job change timing', 'switch job', 'new job window'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['10th House', '6th House', '11th House', 'D-10'],
    optionalFactors: ['Saturn'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[12], k, l, o)
  },
  {
    id: 'q_career_growth_periods',
    domainId: 'career_job',
    sphereId: 'artha',
    canonicalIntent: 'CAREER_GROWTH_PERIODS',
    categoryId: 'timing_windows',
    categoryNameEn: 'Career Growth Periods',
    categoryNameHi: 'उन्नति एवं पदोन्नति काल',
    questionEn: 'When are stronger career-growth periods?',
    questionHi: 'कार्यक्षेत्र में पदोन्नति एवं तीव्र उन्नति के प्रबल काल-खंड कब हैं?',
    shortLabelEn: 'Career Growth Periods',
    shortLabelHi: 'पदोन्नति व उन्नति काल',
    intent: 'TIMING',
    iconName: 'Award',
    keywords: ['promotion timing', 'career growth', 'appraisal period'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['10th House', '11th House', 'Sun', 'Jupiter', 'D-10'],
    optionalFactors: ['Mercury'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[13], k, l, o)
  },
  {
    id: 'q_career_direction',
    domainId: 'career_job',
    sphereId: 'artha',
    canonicalIntent: 'CAREER_DIRECTION',
    categoryId: 'career_direction',
    categoryNameEn: 'Career Direction',
    categoryNameHi: 'कार्यक्षेत्र दिशा',
    questionEn: 'What career direction is indicated?',
    questionHi: 'कुंडली के अनुसार कौन सा कार्यक्षेत्र अथवा उद्योग सर्वाधिक अनुकूल है?',
    shortLabelEn: 'Career Direction',
    shortLabelHi: 'आजीविका की दिशा',
    intent: 'NATURE',
    iconName: 'Compass',
    keywords: ['career direction', 'which field', 'best career'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['10th Lord', 'Amatya Karaka', 'D-10 10th House'],
    optionalFactors: ['Sun', 'Mercury'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[14], k, l, o)
  },
  {
    id: 'q_employment_vs_business',
    domainId: 'career_job',
    sphereId: 'artha',
    canonicalIntent: 'EMPLOYMENT_VS_BUSINESS',
    categoryId: 'career_direction',
    categoryNameEn: 'Job vs. Business',
    categoryNameHi: 'नौकरी बनाम व्यापार',
    questionEn: 'Employment or entrepreneurship?',
    questionHi: 'नौकरी (सेवा) अथवा स्वतंत्र व्यापार (बिजनेस) में से क्या अधिक फलदायी है?',
    shortLabelEn: 'Job or Business?',
    shortLabelHi: 'नौकरी या व्यापार?',
    intent: 'COMPARISON',
    iconName: 'Award',
    keywords: ['job vs business', 'employment or entrepreneurship', 'startup vs job'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['6th House', '7th House', '10th House', 'Mercury', 'Mars'],
    optionalFactors: ['3rd House'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[15], k, l, o)
  },
  {
    id: 'q_career_strengths',
    domainId: 'career_job',
    sphereId: 'artha',
    canonicalIntent: 'CAREER_STRENGTHS',
    categoryId: 'career_direction',
    categoryNameEn: 'Professional Strengths',
    categoryNameHi: 'व्यावसायिक दक्षता',
    questionEn: 'What professional strengths are indicated?',
    questionHi: 'कार्यक्षेत्र में कौन सी विशेष योग्यताएं एवं शक्तियां सफलता दिलाती हैं?',
    shortLabelEn: 'Professional Strengths',
    shortLabelHi: 'व्यावसायिक शक्तियां',
    intent: 'DEVELOPMENT',
    iconName: 'Shield',
    keywords: ['professional strengths', 'career skills', 'leadership qualities'],
    karmicElasticity: 'dridha',
    requiredFactors: ['10th House', 'Mercury', 'Sun', 'Mars'],
    optionalFactors: ['D-10'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[16], k, l, o)
  },
  {
    id: 'q_career_transitions',
    domainId: 'career_job',
    sphereId: 'artha',
    canonicalIntent: 'CAREER_TRANSITIONS',
    categoryId: 'transitions_pivots',
    categoryNameEn: 'Career Transitions',
    categoryNameHi: 'कार्यक्षेत्र बदलाव',
    questionEn: 'Are major career transitions ahead?',
    questionHi: 'क्या कार्यक्षेत्र में कोई बड़ा रणनीतिक बदलाव अथवा उद्योग परिवर्तन संभावित है?',
    shortLabelEn: 'Career Transitions',
    shortLabelHi: 'करियर बदलाव',
    intent: 'TRANSITION',
    iconName: 'Clock',
    keywords: ['career transition', 'industry change', 'role shift'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['10th House', '8th/12th House', 'Saturn transit'],
    optionalFactors: ['Rahu'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[17], k, l, o)
  },
  {
    id: 'q_career_responsibilities_change',
    domainId: 'career_job',
    sphereId: 'artha',
    canonicalIntent: 'CAREER_RESPONSIBILITIES_CHANGE',
    categoryId: 'current_phase',
    categoryNameEn: 'Responsibilities Evolution',
    categoryNameHi: 'दायित्वों का विस्तार',
    questionEn: 'When may my professional responsibilities change?',
    questionHi: 'कार्यक्षेत्र में नवीन जिम्मेदारियां एवं अधिकार कब विस्तारित होंगे?',
    shortLabelEn: 'Responsibilities Change',
    shortLabelHi: 'दायित्वों में परिवर्तन',
    intent: 'TIMING',
    iconName: 'Target',
    keywords: ['responsibility change', 'new role', 'executive authority'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['10th House', 'Saturn', 'Sun'],
    optionalFactors: ['D-10'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[18], k, l, o)
  },
  {
    id: 'q_work_environment',
    domainId: 'career_job',
    sphereId: 'artha',
    canonicalIntent: 'WORK_ENVIRONMENT',
    categoryId: 'career_direction',
    categoryNameEn: 'Workplace Environment',
    categoryNameHi: 'कार्यस्थल वातावरण',
    questionEn: 'What type of work environment is indicated?',
    questionHi: 'किस प्रकार का संगठनात्मक परिवेश व कार्य-संस्कृति सर्वाधिक अनुकूल है?',
    shortLabelEn: 'Work Environment',
    shortLabelHi: 'कार्य वातावरण',
    intent: 'NATURE',
    iconName: 'Briefcase',
    keywords: ['work culture', 'corporate vs remote', 'work environment'],
    karmicElasticity: 'adridha',
    requiredFactors: ['10th House', '6th House', 'Moon'],
    optionalFactors: ['Mercury'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[19], k, l, o)
  },

  // ==========================================================================
  // 3. COMMERCIAL VENTURE & STARTUPS (business) — 8 Curated Questions
  // ==========================================================================
  {
    id: 'q_business_feasibility',
    domainId: 'business',
    sphereId: 'artha',
    canonicalIntent: 'BUSINESS_FEASIBILITY',
    categoryId: 'career_direction',
    categoryNameEn: 'Entrepreneurship Support',
    categoryNameHi: 'उद्यमिता योग',
    questionEn: 'Is entrepreneurship supported in my chart?',
    questionHi: 'क्या मेरी कुंडली में स्वतंत्र व्यापार/स्टार्टअप के योग हैं?',
    shortLabelEn: 'Entrepreneurship Support',
    shortLabelHi: 'उद्यमिता योग',
    intent: 'POSSIBILITY',
    iconName: 'Award',
    keywords: ['entrepreneurship supported', 'business yoga', 'startup feasibility'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['7th House', '10th House', '3rd House', 'Mercury'],
    optionalFactors: ['Mars'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[20], k, l, o)
  },
  {
    id: 'q_business_startup_timing',
    domainId: 'business',
    sphereId: 'artha',
    canonicalIntent: 'BUSINESS_STARTUP_TIMING',
    categoryId: 'timing_windows',
    categoryNameEn: 'Launch Timing',
    categoryNameHi: 'व्यवसाय प्रारंभ काल',
    questionEn: 'When is a good time to start a business?',
    questionHi: 'नवीन व्यापार अथवा स्टार्टअप प्रारंभ करने हेतु शुभ समय कौन सा है?',
    shortLabelEn: 'Startup Launch Timing',
    shortLabelHi: 'स्टार्टअप प्रारंभ समय',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['start business timing', 'launch startup', 'when start company'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['7th House', '3rd House', '11th House', 'Mercury'],
    optionalFactors: ['Sun'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[21], k, l, o)
  },
  {
    id: 'q_business_expansion_timing',
    domainId: 'business',
    sphereId: 'artha',
    canonicalIntent: 'BUSINESS_EXPANSION_TIMING',
    categoryId: 'timing_windows',
    categoryNameEn: 'Expansion Timing',
    categoryNameHi: 'व्यापार विस्तार काल',
    questionEn: 'When is a good time to expand?',
    questionHi: 'व्यावसायिक विस्तार एवं नई शाखा/उत्पाद प्रारंभ करने का अनुकूल समय क्या है?',
    shortLabelEn: 'Business Expansion Timing',
    shortLabelHi: 'व्यापार विस्तार समय',
    intent: 'TIMING',
    iconName: 'Coins',
    keywords: ['business expansion', 'scale business', 'funding timing'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['11th House', '10th House', 'Jupiter'],
    optionalFactors: ['Mercury'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[22], k, l, o)
  },
  {
    id: 'q_business_direction',
    domainId: 'business',
    sphereId: 'artha',
    canonicalIntent: 'BUSINESS_DIRECTION',
    categoryId: 'career_direction',
    categoryNameEn: 'Business Direction',
    categoryNameHi: 'व्यापारिक क्षेत्र',
    questionEn: 'What type of business direction is indicated?',
    questionHi: 'कुंडली के अनुसार कौन सा व्यापार क्षेत्र (सेवा, उत्पाद, तकनीकी) सर्वाधिक फलदायी है?',
    shortLabelEn: 'Business Direction',
    shortLabelHi: 'व्यापारिक क्षेत्र',
    intent: 'NATURE',
    iconName: 'Compass',
    keywords: ['business direction', 'type of business', 'sector alignment'],
    karmicElasticity: 'adridha',
    requiredFactors: ['7th Lord', 'Mercury', 'Mars', 'Venus'],
    optionalFactors: ['D-10'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[23], k, l, o)
  },
  {
    id: 'q_business_partnership',
    domainId: 'business',
    sphereId: 'artha',
    canonicalIntent: 'BUSINESS_PARTNERSHIP',
    categoryId: 'partner_compatibility',
    categoryNameEn: 'Partnership Feasibility',
    categoryNameHi: 'साझेदारी व्यापार',
    questionEn: 'Is partnership-based business supported?',
    questionHi: 'क्या साझेदारी (पार्टनरशिप) में व्यापार करना शुभ एवं लाभकारी रहेगा?',
    shortLabelEn: 'Partnership Business?',
    shortLabelHi: 'साझेदारी में व्यापार?',
    intent: 'POSSIBILITY',
    iconName: 'Users',
    keywords: ['partnership business', 'solo vs partnership', 'business partners'],
    karmicElasticity: 'adridha',
    requiredFactors: ['7th House', '11th House', 'Mercury', 'Jupiter'],
    optionalFactors: ['Rahu'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[24], k, l, o)
  },
  {
    id: 'q_business_cycle_state',
    domainId: 'business',
    sphereId: 'artha',
    canonicalIntent: 'BUSINESS_CYCLE_STATE',
    categoryId: 'current_phase',
    categoryNameEn: 'Business Cycle Now',
    categoryNameHi: 'वर्तमान व्यापार चक्र',
    questionEn: 'What is happening in my business cycle now?',
    questionHi: 'वर्तमान में मेरे व्यावसायिक चक्र में क्या स्थिति चल रही है?',
    shortLabelEn: 'Business Cycle Now',
    shortLabelHi: 'वर्तमान व्यापार स्थिति',
    intent: 'CURRENT_STATE',
    iconName: 'Target',
    keywords: ['business cycle', 'business state now', 'market traction'],
    karmicElasticity: 'adridha',
    requiredFactors: ['Current Dasha', 'Mercury Gochar', '7th House'],
    optionalFactors: ['Jupiter'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[25], k, l, o)
  },
  {
    id: 'q_business_future_periods',
    domainId: 'business',
    sphereId: 'artha',
    canonicalIntent: 'BUSINESS_FUTURE_PERIODS',
    categoryId: 'timing_windows',
    categoryNameEn: 'Future Business Periods',
    categoryNameHi: 'भावी व्यापार काल',
    questionEn: 'What major business periods lie ahead?',
    questionHi: 'आगामी समय में व्यापारिक उन्नति व अवसरों के प्रमुख काल-खंड कौन से हैं?',
    shortLabelEn: 'Future Business Periods',
    shortLabelHi: 'भावी व्यापार काल',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['future business periods', 'business opportunities timeline'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['Vimshottari Dasha', '7th/11th House'],
    optionalFactors: ['Jupiter Transit'],
    timingRequired: true,
    currentStateSupported: false,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[26], k, l, o)
  },
  {
    id: 'q_business_continuity',
    domainId: 'business',
    sphereId: 'artha',
    canonicalIntent: 'BUSINESS_CONTINUITY',
    categoryId: 'stability_continuity',
    categoryNameEn: 'Business Continuity',
    categoryNameHi: 'दीर्घकालीन व्यापार निरंतरता',
    questionEn: 'What factors support long-term business continuity?',
    questionHi: 'दीर्घकालीन व्यापारिक स्थिरता एवं ब्रांड प्रतिष्ठा में कौन से ग्रह सहायक हैं?',
    shortLabelEn: 'Business Continuity',
    shortLabelHi: 'व्यापार निरंतरता',
    intent: 'STABILITY',
    iconName: 'Shield',
    keywords: ['business continuity', 'brand stability', 'commercial resilience'],
    karmicElasticity: 'dridha',
    requiredFactors: ['10th House', 'Saturn', 'Jupiter'],
    optionalFactors: ['Mercury'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[27], k, l, o)
  },

  // ==========================================================================
  // 4. WEALTH & LIQUIDITY (wealth_finance) — 8 Curated Questions
  // ==========================================================================
  {
    id: 'q_wealth_current_finances',
    domainId: 'wealth_finance',
    sphereId: 'artha',
    canonicalIntent: 'WEALTH_CURRENT_FINANCES',
    categoryId: 'current_phase',
    categoryNameEn: 'Current Financial Phase',
    categoryNameHi: 'वर्तमान आर्थिक स्थिति',
    questionEn: 'What is happening with my finances now?',
    questionHi: 'वर्तमान में मेरी आर्थिक स्थिति एवं धन संचय में क्या प्रभाव सक्रिय हैं?',
    shortLabelEn: 'Finances Now',
    shortLabelHi: 'वर्तमान आर्थिक स्थिति',
    intent: 'CURRENT_STATE',
    iconName: 'Coins',
    keywords: ['finances now', 'current money state', 'financial situation'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['2nd House', '11th House', 'Jupiter', 'Mercury'],
    optionalFactors: ['Current Dasha'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[28], k, l, o)
  },
  {
    id: 'q_wealth_building_periods',
    domainId: 'wealth_finance',
    sphereId: 'artha',
    canonicalIntent: 'WEALTH_BUILDING_PERIODS',
    categoryId: 'timing_windows',
    categoryNameEn: 'Wealth Building Windows',
    categoryNameHi: 'धन संचय काल',
    questionEn: 'When are stronger wealth-building periods?',
    questionHi: 'धन संचय, कोष वृद्धि एवं वित्तीय सुदृढ़ता के प्रमुख काल-खंड कब हैं?',
    shortLabelEn: 'Wealth Building Windows',
    shortLabelHi: 'धन संचय काल',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['wealth building periods', 'money growth timing', 'saving windows'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['2nd House', '11th House', 'Jupiter', 'D-2 Hora'],
    optionalFactors: ['Venus'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[29], k, l, o)
  },
  {
    id: 'q_wealth_income_periods',
    domainId: 'wealth_finance',
    sphereId: 'artha',
    canonicalIntent: 'WEALTH_INCOME_PERIODS',
    categoryId: 'timing_windows',
    categoryNameEn: 'Income Expansion Windows',
    categoryNameHi: 'आय वृद्धि काल',
    questionEn: 'When are stronger income periods?',
    questionHi: 'नियमित आय एवं लाभ में तीव्र वृद्धि के प्रबल अवसर कब हैं?',
    shortLabelEn: 'Income Expansion',
    shortLabelHi: 'आय वृद्धि अवसर',
    intent: 'TIMING',
    iconName: 'Coins',
    keywords: ['income periods', 'revenue growth', 'higher salary timing'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['11th House', '11th Lord', 'Jupiter', 'Mercury'],
    optionalFactors: ['Sun'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[30], k, l, o)
  },
  {
    id: 'q_wealth_asset_building',
    domainId: 'wealth_finance',
    sphereId: 'artha',
    canonicalIntent: 'WEALTH_ASSET_BUILDING',
    categoryId: 'wealth_assets',
    categoryNameEn: 'Assets & Speculation',
    categoryNameHi: 'परिसंपत्ति एवं शेयर बाजार',
    questionEn: 'When are major asset-building periods & stock market investments favored?',
    questionHi: 'दीर्घकालीन परिसंपत्ति निर्माण एवं शेयर बाजार/इक्विटी निवेश के अनुकूल समय क्या हैं?',
    shortLabelEn: 'Assets & Stock Market',
    shortLabelHi: 'परिसंपत्ति व शेयर बाजार',
    intent: 'POSSIBILITY',
    iconName: 'Coins',
    keywords: ['asset building', 'stock market', 'equity investment', 'share market'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['5th House', '11th House', '2nd House', 'Mercury', 'Rahu'],
    optionalFactors: ['8th House'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[31], k, l, o)
  },
  {
    id: 'q_wealth_active_themes',
    domainId: 'wealth_finance',
    sphereId: 'artha',
    canonicalIntent: 'WEALTH_ACTIVE_THEMES',
    categoryId: 'current_phase',
    categoryNameEn: 'Active Financial Themes',
    categoryNameHi: 'सक्रिय वित्तीय योग',
    questionEn: 'What financial themes are active now?',
    questionHi: 'वर्तमान में वित्तीय निर्णयों से जुड़े कौन से ग्रह-प्रभाव सक्रिय हैं?',
    shortLabelEn: 'Active Financial Themes',
    shortLabelHi: 'सक्रिय वित्तीय प्रभाव',
    intent: 'CURRENT_STATE',
    iconName: 'Target',
    keywords: ['financial themes active', 'money cycles today'],
    karmicElasticity: 'adridha',
    requiredFactors: ['Current Dasha', 'Jupiter Transit'],
    optionalFactors: ['2nd House'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[32], k, l, o)
  },
  {
    id: 'q_wealth_financial_transitions',
    domainId: 'wealth_finance',
    sphereId: 'artha',
    canonicalIntent: 'WEALTH_FINANCIAL_TRANSITIONS',
    categoryId: 'transitions_pivots',
    categoryNameEn: 'Financial Transitions',
    categoryNameHi: 'वित्तीय रूपांतरण',
    questionEn: 'When are major financial transitions indicated?',
    questionHi: 'आर्थिक स्थिति में कोई बड़ा बदलाव अथवा महत्वपूर्ण निवेश काल कब है?',
    shortLabelEn: 'Financial Transitions',
    shortLabelHi: 'वित्तीय बदलाव काल',
    intent: 'TRANSITION',
    iconName: 'Clock',
    keywords: ['financial transitions', 'money shifts', 'major investment timing'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['2nd/11th/8th Houses', 'Dasha Sandhi'],
    optionalFactors: ['Saturn Transit'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[33], k, l, o)
  },
  {
    id: 'q_wealth_stability_factors',
    domainId: 'wealth_finance',
    sphereId: 'artha',
    canonicalIntent: 'WEALTH_STABILITY_FACTORS',
    categoryId: 'stability_continuity',
    categoryNameEn: 'Financial Stability',
    categoryNameHi: 'वित्तीय स्थिरता',
    questionEn: 'What supports long-term financial stability?',
    questionHi: 'दीर्घकालीन वित्तीय सुरक्षा एवं स्थिरता में कौन से ग्रह सहायक हैं?',
    shortLabelEn: 'Financial Stability',
    shortLabelHi: 'वित्तीय स्थिरता',
    intent: 'STABILITY',
    iconName: 'Shield',
    keywords: ['financial stability', 'wealth preservation', 'long term security'],
    karmicElasticity: 'dridha',
    requiredFactors: ['2nd House', 'Jupiter', 'Saturn'],
    optionalFactors: ['D-2'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[34], k, l, o)
  },
  {
    id: 'q_wealth_caution_periods',
    domainId: 'wealth_finance',
    sphereId: 'artha',
    canonicalIntent: 'WEALTH_CAUTION_PERIODS',
    categoryId: 'challenges_mitigation',
    categoryNameEn: 'Financial Caution',
    categoryNameHi: 'वित्तीय सावधानी काल',
    questionEn: 'What periods require greater financial caution?',
    questionHi: 'किन काल-खंडों में अनावश्यक व्यय अथवा वित्तीय जोखिम से सतर्क रहना आवश्यक है?',
    shortLabelEn: 'Financial Caution Periods',
    shortLabelHi: 'सावधानी के काल',
    intent: 'CHALLENGE',
    iconName: 'Shield',
    keywords: ['financial caution', 'money loss periods', 'risk containment'],
    karmicElasticity: 'dridha',
    requiredFactors: ['12th House (Vyaya)', '8th House', 'Rahu'],
    optionalFactors: ['Mars'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[35], k, l, o)
  },

  // ==========================================================================
  // 5. CHILDREN & LEGACY (children) — 7 Curated Questions
  // ==========================================================================
  {
    id: 'q_children_themes',
    domainId: 'children',
    sphereId: 'dharma',
    canonicalIntent: 'CHILDREN_THEMES',
    categoryId: 'nature_patterns',
    categoryNameEn: 'Progeny Indications',
    categoryNameHi: 'संतान योग',
    questionEn: 'What children-related themes are indicated?',
    questionHi: 'कुंडली के अनुसार संतान सुख एवं वंश वृद्धि के क्या शास्त्रीय संकेत हैं?',
    shortLabelEn: 'Children Themes',
    shortLabelHi: 'संतान संकेत',
    intent: 'POSSIBILITY',
    iconName: 'Crown',
    keywords: ['children themes', 'progeny promise', 'santan sukha'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['5th House', 'Jupiter', 'D-7 Saptamsha'],
    optionalFactors: ['5th Lord'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[36], k, l, o)
  },
  {
    id: 'q_children_progeny_timing',
    domainId: 'children',
    sphereId: 'dharma',
    canonicalIntent: 'CHILDREN_PROGENY_TIMING',
    categoryId: 'timing_windows',
    categoryNameEn: 'Parenthood Timing',
    categoryNameHi: 'संतान प्राप्ति समय',
    questionEn: 'When are children-related periods activated?',
    questionHi: 'संतान प्राप्ति, गर्भाधान एवं पितृत्व/मातृत्व हेतु अनुकूल समय कौन सा है?',
    shortLabelEn: 'Parenthood Timing',
    shortLabelHi: 'संतान प्राप्ति काल',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['children timing', 'pregnancy timing', 'santan prapti date'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['5th House', 'Jupiter', 'D-7'],
    optionalFactors: ['Sun', 'Mars'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[37], k, l, o)
  },
  {
    id: 'q_children_family_phase',
    domainId: 'children',
    sphereId: 'dharma',
    canonicalIntent: 'CHILDREN_FAMILY_PHASE',
    categoryId: 'current_phase',
    categoryNameEn: 'Family Expansion Phase',
    categoryNameHi: 'वर्तमान पारिवारिक चरण',
    questionEn: 'What is the current family/parenthood phase?',
    questionHi: 'वर्तमान में संतान एवं पारिवारिक विस्तार का क्या चरण सक्रिय है?',
    shortLabelEn: 'Family Phase Now',
    shortLabelHi: 'वर्तमान पारिवारिक चरण',
    intent: 'CURRENT_STATE',
    iconName: 'Target',
    keywords: ['family phase now', 'current parenthood state'],
    karmicElasticity: 'adridha',
    requiredFactors: ['Current Dasha', '5th House Gochar'],
    optionalFactors: ['Jupiter'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[38], k, l, o)
  },
  {
    id: 'q_children_education_themes',
    domainId: 'children',
    sphereId: 'dharma',
    canonicalIntent: 'CHILDREN_EDUCATION_THEMES',
    categoryId: 'academic_direction',
    categoryNameEn: 'Child Talents',
    categoryNameHi: 'संतान प्रतिभा व शिक्षा',
    questionEn: 'What educational themes are indicated for children?',
    questionHi: 'संतान के बौद्धिक विकास, प्रतिभा एवं शैक्षणिक क्षमताओं में कौन से ग्रह योग सहायक हैं?',
    shortLabelEn: 'Child Education & Talents',
    shortLabelHi: 'संतान शिक्षा व प्रतिभा',
    intent: 'DEVELOPMENT',
    iconName: 'Crown',
    keywords: ['child education', 'child talent', 'intellect of children'],
    karmicElasticity: 'adridha',
    requiredFactors: ['5th House', 'Mercury', 'Jupiter', 'D-7'],
    optionalFactors: ['Sun'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[39], k, l, o)
  },
  {
    id: 'q_children_parent_child_bond',
    domainId: 'children',
    sphereId: 'dharma',
    canonicalIntent: 'CHILDREN_PARENT_CHILD_BOND',
    categoryId: 'family_dynamics',
    categoryNameEn: 'Parent-Child Bonding',
    categoryNameHi: 'माता-पिता संतान संबंध',
    questionEn: 'What parent-child themes are currently active?',
    questionHi: 'माता-पिता एवं संतान के मध्य संवाद व आत्मीयता को क्या ग्रह-स्थितियां पोषित करती हैं?',
    shortLabelEn: 'Parent-Child Harmony',
    shortLabelHi: 'संतान सौहार्द',
    intent: 'PARTNER_DYNAMICS',
    iconName: 'Users',
    keywords: ['parent child bond', 'harmony with children', 'family bonding'],
    karmicElasticity: 'adridha',
    requiredFactors: ['5th House', '9th House', 'Moon', 'Jupiter'],
    optionalFactors: ['Sun'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[40], k, l, o)
  },
  {
    id: 'q_children_future_periods',
    domainId: 'children',
    sphereId: 'dharma',
    canonicalIntent: 'CHILDREN_FUTURE_PERIODS',
    categoryId: 'timing_windows',
    categoryNameEn: 'Future Family Milestones',
    categoryNameHi: 'भावी पारिवारिक मील के पत्थर',
    questionEn: 'What major family periods lie ahead?',
    questionHi: 'संतान एवं परिवार के भविष्य से जुड़े महत्वपूर्ण काल-खंड कौन से हैं?',
    shortLabelEn: 'Future Family Milestones',
    shortLabelHi: 'भावी पारिवारिक काल',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['future family milestones', 'children future timing'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['5th House', 'Jupiter Transits'],
    optionalFactors: ['D-7'],
    timingRequired: true,
    currentStateSupported: false,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[41], k, l, o)
  },
  {
    id: 'q_children_legacy_values',
    domainId: 'children',
    sphereId: 'dharma',
    canonicalIntent: 'CHILDREN_LEGACY_VALUES',
    categoryId: 'stability_continuity',
    categoryNameEn: 'Legacy & Lineage',
    categoryNameHi: 'कुल परंपरा एवं संस्कार',
    questionEn: 'What legacy themes are indicated?',
    questionHi: 'आगामी पीढ़ी को हस्तांतरित होने वाले सांस्कृतिक व नैतिक संस्कारों के क्या संकेत हैं?',
    shortLabelEn: 'Legacy & Lineage Values',
    shortLabelHi: 'कुल संस्कार व परंपरा',
    intent: 'STABILITY',
    iconName: 'Shield',
    keywords: ['legacy themes', 'family lineage', 'next generation values'],
    karmicElasticity: 'dridha',
    requiredFactors: ['9th House', '2nd House', 'Jupiter', 'Sun'],
    optionalFactors: ['D-12'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[42], k, l, o)
  },

  // ==========================================================================
  // 6. HIGHER EDUCATION & EXAMS (education) — 7 Curated Questions
  // ==========================================================================
  {
    id: 'q_education_current_phase',
    domainId: 'education',
    sphereId: 'dharma',
    canonicalIntent: 'EDUCATION_CURRENT_PHASE',
    categoryId: 'current_phase',
    categoryNameEn: 'Current Academic Phase',
    categoryNameHi: 'वर्तमान अध्ययन स्थिति',
    questionEn: 'What is my current education phase?',
    questionHi: 'वर्तमान में मेरी विद्या, अध्ययन एवं एकाग्रता में क्या ग्रह प्रभाव सक्रिय हैं?',
    shortLabelEn: 'Education Phase Now',
    shortLabelHi: 'वर्तमान विद्या स्थिति',
    intent: 'CURRENT_STATE',
    iconName: 'BookOpen',
    keywords: ['education phase now', 'current study state'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['4th House', '5th House', 'Mercury'],
    optionalFactors: ['Jupiter'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[43], k, l, o)
  },
  {
    id: 'q_education_higher_studies_timing',
    domainId: 'education',
    sphereId: 'dharma',
    canonicalIntent: 'EDUCATION_HIGHER_STUDIES_TIMING',
    categoryId: 'timing_windows',
    categoryNameEn: 'Higher Education Timing',
    categoryNameHi: 'उच्च विद्या काल',
    questionEn: 'When is a good time for higher education?',
    questionHi: 'उच्च शिक्षा, स्नातकोत्तर अथवा उन्नत अध्ययन प्रारंभ करने हेतु शुभ समय क्या है?',
    shortLabelEn: 'Higher Education Timing',
    shortLabelHi: 'उच्च शिक्षा का समय',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['higher education timing', 'masters degree timing', 'study abroad date'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['9th House', '5th House', 'Jupiter', 'D-24'],
    optionalFactors: ['Mercury'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[44], k, l, o)
  },
  {
    id: 'q_education_exams_study_periods',
    domainId: 'education',
    sphereId: 'dharma',
    canonicalIntent: 'EDUCATION_EXAMS_STUDY_PERIODS',
    categoryId: 'academic_direction',
    categoryNameEn: 'Competitive Exam Windows',
    categoryNameHi: 'प्रतियोगी परीक्षा काल',
    questionEn: 'When are stronger study or exam periods?',
    questionHi: 'प्रतियोगी परीक्षाओं, प्रवेश एवं अध्ययन में सफलता के प्रबल काल-खंड कब हैं?',
    shortLabelEn: 'Exams & Study Periods',
    shortLabelHi: 'परीक्षा व अध्ययन काल',
    intent: 'TIMING',
    iconName: 'BookOpen',
    keywords: ['competitive exams', 'upsc exam', 'exam success timing'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['4th House', '5th House', '6th House', 'Mercury', 'D-24'],
    optionalFactors: ['Mars'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[45], k, l, o)
  },
  {
    id: 'q_education_direction_specialization',
    domainId: 'education',
    sphereId: 'dharma',
    canonicalIntent: 'EDUCATION_DIRECTION_SPECIALIZATION',
    categoryId: 'academic_direction',
    categoryNameEn: 'Academic Direction',
    categoryNameHi: 'शैक्षणिक दिशा',
    questionEn: 'What educational direction is indicated?',
    questionHi: 'कुंडली के अनुसार कौन सा शैक्षणिक विषय (STEM, विधि, वाणिज्य, कला) सर्वाधिक उपयुक्त है?',
    shortLabelEn: 'Academic Direction',
    shortLabelHi: 'विषय व शैक्षणिक दिशा',
    intent: 'NATURE',
    iconName: 'Compass',
    keywords: ['educational direction', 'which subject to study', 'academic specialization'],
    karmicElasticity: 'adridha',
    requiredFactors: ['5th Lord', 'Mercury', 'Jupiter', 'D-24'],
    optionalFactors: ['Sun', 'Mars'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[46], k, l, o)
  },
  {
    id: 'q_education_professional_certification',
    domainId: 'education',
    sphereId: 'dharma',
    canonicalIntent: 'EDUCATION_PROFESSIONAL_CERTIFICATION',
    categoryId: 'academic_direction',
    categoryNameEn: 'Certifications',
    categoryNameHi: 'व्यावसायिक प्रमाणन',
    questionEn: 'Is professional certification supported?',
    questionHi: 'क्या उन्नत व्यावसायिक प्रमाणन एवं कार्यकारी उपाधियां सफलता प्रदायक हैं?',
    shortLabelEn: 'Certifications Support',
    shortLabelHi: 'व्यावसायिक प्रमाणन योग',
    intent: 'POSSIBILITY',
    iconName: 'Award',
    keywords: ['professional certification', 'diploma support', 'executive education'],
    karmicElasticity: 'adridha',
    requiredFactors: ['10th House', '5th House', 'Mercury', 'Saturn'],
    optionalFactors: ['D-24'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[47], k, l, o)
  },
  {
    id: 'q_education_foreign_study',
    domainId: 'education',
    sphereId: 'dharma',
    canonicalIntent: 'EDUCATION_FOREIGN_STUDY',
    categoryId: 'travel_foreign',
    categoryNameEn: 'Foreign Education',
    categoryNameHi: 'विदेश में उच्च शिक्षा',
    questionEn: 'Is foreign education indicated?',
    questionHi: 'क्या अंतर्राष्ट्रीय विश्वविद्यालय अथवा विदेश में उच्च अध्ययन के योग हैं?',
    shortLabelEn: 'Foreign Education',
    shortLabelHi: 'विदेश में विद्या योग',
    intent: 'POSSIBILITY',
    iconName: 'Plane',
    keywords: ['foreign education', 'study abroad', 'international university'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['9th House', '12th House', '5th House', 'Rahu', 'Jupiter'],
    optionalFactors: ['Moon'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[48], k, l, o)
  },
  {
    id: 'q_education_study_to_career',
    domainId: 'education',
    sphereId: 'dharma',
    canonicalIntent: 'EDUCATION_STUDY_TO_CAREER',
    categoryId: 'transitions_pivots',
    categoryNameEn: 'Study to Career Transition',
    categoryNameHi: 'शिक्षा से कार्यक्षेत्र में प्रवेश',
    questionEn: 'When does the study-to-career transition become active?',
    questionHi: 'अध्ययन पूर्ण कर कार्यक्षेत्र में सफल प्रवेश का समय कब प्रारंभ होता है?',
    shortLabelEn: 'Study to Career Pivot',
    shortLabelHi: 'विद्या से करियर प्रवेश',
    intent: 'TRANSITION',
    iconName: 'Briefcase',
    keywords: ['study to career', 'graduation to job', 'first job timing'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['5th-10th House link', 'Dasha transition'],
    optionalFactors: ['D-10'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[49], k, l, o)
  },

  // ==========================================================================
  // 7. PROPERTY & INHERITANCE (house_property) — 8 Curated Questions
  // ==========================================================================
  {
    id: 'q_property_current_matters',
    domainId: 'house_property',
    sphereId: 'artha',
    canonicalIntent: 'PROPERTY_CURRENT_MATTERS',
    categoryId: 'current_phase',
    categoryNameEn: 'Current Property Phase',
    categoryNameHi: 'वर्तमान संपत्ति स्थिति',
    questionEn: 'What is happening with my property matters now?',
    questionHi: 'वर्तमान में भूमि, आवास एवं अचल संपत्ति से जुड़े क्या प्रभाव सक्रिय हैं?',
    shortLabelEn: 'Property Matters Now',
    shortLabelHi: 'वर्तमान संपत्ति स्थिति',
    intent: 'CURRENT_STATE',
    iconName: 'Home',
    keywords: ['property matters now', 'current housing phase'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['4th House', 'Mars', 'D-4'],
    optionalFactors: ['Current Dasha'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[50], k, l, o)
  },
  {
    id: 'q_property_buy_timing',
    domainId: 'house_property',
    sphereId: 'artha',
    canonicalIntent: 'PROPERTY_BUY_TIMING',
    categoryId: 'property_vehicles',
    categoryNameEn: 'Property Purchase Timing',
    categoryNameHi: 'संपत्ति क्रय समय',
    questionEn: 'When is a good time to buy property?',
    questionHi: 'भूमि, भवन, फ्लैट अथवा अचल संपत्ति क्रय करने हेतु शुभ समय कौन सा है?',
    shortLabelEn: 'Property Purchase Timing',
    shortLabelHi: 'संपत्ति क्रय का शुभ समय',
    intent: 'TIMING',
    iconName: 'Home',
    keywords: ['buy property', 'buy house', 'real estate investment'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['4th House', 'Mars', 'Saturn', 'D-4'],
    optionalFactors: ['2nd House'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[51], k, l, o)
  },
  {
    id: 'q_property_sell_timing',
    domainId: 'house_property',
    sphereId: 'artha',
    canonicalIntent: 'PROPERTY_SELL_TIMING',
    categoryId: 'property_vehicles',
    categoryNameEn: 'Property Sale Timing',
    categoryNameHi: 'संपत्ति विक्रय समय',
    questionEn: 'When is a good time to sell property?',
    questionHi: 'अचल संपत्ति का विक्रय कर लाभ प्राप्त करने का अनुकूल समय क्या है?',
    shortLabelEn: 'Property Sale Timing',
    shortLabelHi: 'संपत्ति विक्रय समय',
    intent: 'TIMING',
    iconName: 'Coins',
    keywords: ['sell property', 'property disposal', 'real estate sale'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['4th House', '11th House', 'Mars'],
    optionalFactors: ['Mercury'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[52], k, l, o)
  },
  {
    id: 'q_property_relocation_timing',
    domainId: 'house_property',
    sphereId: 'artha',
    canonicalIntent: 'PROPERTY_RELOCATION_TIMING',
    categoryId: 'transitions_pivots',
    categoryNameEn: 'Relocation Timing',
    categoryNameHi: 'निवास परिवर्तन समय',
    questionEn: 'When is relocation indicated?',
    questionHi: 'घर बदलने अथवा नए नगर/स्थान पर स्थानांतरण के योग कब हैं?',
    shortLabelEn: 'Relocation Timing',
    shortLabelHi: 'स्थान परिवर्तन काल',
    intent: 'TRANSITION',
    iconName: 'Compass',
    keywords: ['relocation timing', 'change residence', 'shifting house'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['4th House', '3rd/12th House', 'Moon'],
    optionalFactors: ['Rahu'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[53], k, l, o)
  },
  {
    id: 'q_property_residence_themes',
    domainId: 'house_property',
    sphereId: 'artha',
    canonicalIntent: 'PROPERTY_RESIDENCE_THEMES',
    categoryId: 'current_phase',
    categoryNameEn: 'Residence Environment',
    categoryNameHi: 'आवास वातावरण',
    questionEn: 'What residence themes are currently active?',
    questionHi: 'वर्तमान निवास स्थान एवं वास्तु सुख में क्या ग्रह-प्रभाव सक्रिय हैं?',
    shortLabelEn: 'Residence Themes',
    shortLabelHi: 'आवास वातावरण',
    intent: 'CURRENT_STATE',
    iconName: 'Home',
    keywords: ['residence themes', 'vastu comfort', 'home atmosphere'],
    karmicElasticity: 'adridha',
    requiredFactors: ['4th House', 'Venus', 'Moon'],
    optionalFactors: ['Jupiter'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[54], k, l, o)
  },
  {
    id: 'q_property_inheritance_ancestral',
    domainId: 'house_property',
    sphereId: 'artha',
    canonicalIntent: 'PROPERTY_INHERITANCE_ANCESTRAL',
    categoryId: 'wealth_assets',
    categoryNameEn: 'Inheritance & Ancestral',
    categoryNameHi: 'पैतृक संपत्ति व वसीयत',
    questionEn: 'What inheritance themes are indicated?',
    questionHi: 'पैतृक भूमि, वसीयत अथवा पारिवारिक विरासत में मिलने वाली संपत्ति के क्या संकेत हैं?',
    shortLabelEn: 'Inheritance Themes',
    shortLabelHi: 'पैतृक संपत्ति संकेत',
    intent: 'POSSIBILITY',
    iconName: 'Shield',
    keywords: ['inheritance themes', 'ancestral property', 'paitrik sampatti'],
    karmicElasticity: 'dridha',
    requiredFactors: ['8th House', '4th House', 'Saturn', 'Jupiter'],
    optionalFactors: ['D-8'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[55], k, l, o)
  },
  {
    id: 'q_property_future_periods',
    domainId: 'house_property',
    sphereId: 'artha',
    canonicalIntent: 'PROPERTY_FUTURE_PERIODS',
    categoryId: 'timing_windows',
    categoryNameEn: 'Future Property Milestones',
    categoryNameHi: 'भावी संपत्ति मील के पत्थर',
    questionEn: 'What major property periods lie ahead?',
    questionHi: 'आगामी वर्षों में भवन निर्माण अथवा भूमि सौदों के मुख्य अवसर कब बनेंगे?',
    shortLabelEn: 'Future Property Milestones',
    shortLabelHi: 'भावी संपत्ति अवसर',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['future property periods', 'upcoming real estate windows'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['4th House', 'Mars', 'Jupiter'],
    optionalFactors: ['D-4'],
    timingRequired: true,
    currentStateSupported: false,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[56], k, l, o)
  },
  {
    id: 'q_property_residence_stability',
    domainId: 'house_property',
    sphereId: 'artha',
    canonicalIntent: 'PROPERTY_RESIDENCE_STABILITY',
    categoryId: 'stability_continuity',
    categoryNameEn: 'Residence Stability',
    categoryNameHi: 'आवास स्थिरता',
    questionEn: 'Is my current residence phase stable or transitional?',
    questionHi: 'क्या वर्तमान आवास स्थिति दीर्घकालीन स्थिर है अथवा निकट भविष्य में स्थानांतरण के संकेत हैं?',
    shortLabelEn: 'Residence Stability Check',
    shortLabelHi: 'आवास स्थिरता परीक्षण',
    intent: 'STABILITY',
    iconName: 'Shield',
    keywords: ['residence stable', 'home transition', 'long term home'],
    karmicElasticity: 'adridha',
    requiredFactors: ['4th House Fixed Signs', 'Saturn'],
    optionalFactors: ['Moon'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[57], k, l, o)
  },

  // ==========================================================================
  // 8. VEHICLES & CONVEYANCES (vehicle) — 5 Curated Questions
  // ==========================================================================
  {
    id: 'q_vehicle_buy_timing',
    domainId: 'vehicle',
    sphereId: 'artha',
    canonicalIntent: 'VEHICLE_BUY_TIMING',
    categoryId: 'timing_windows',
    categoryNameEn: 'Vehicle Purchase Timing',
    categoryNameHi: 'वाहन क्रय समय',
    questionEn: 'When is a good time to buy a vehicle?',
    questionHi: 'नवीन वाहन अथवा सवारी साधन क्रय करने हेतु शुभ समय कौन सा है?',
    shortLabelEn: 'Vehicle Purchase Timing',
    shortLabelHi: 'वाहन क्रय का शुभ समय',
    intent: 'TIMING',
    iconName: 'Compass',
    keywords: ['buy vehicle', 'buy car', 'vahana sukha timing'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['4th House', 'Venus', 'Moon'],
    optionalFactors: ['Current Dasha'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[58], k, l, o)
  },
  {
    id: 'q_vehicle_active_themes',
    domainId: 'vehicle',
    sphereId: 'artha',
    canonicalIntent: 'VEHICLE_ACTIVE_THEMES',
    categoryId: 'current_phase',
    categoryNameEn: 'Active Conveyance Themes',
    categoryNameHi: 'सक्रिय वाहन सुख',
    questionEn: 'What vehicle/conveyance themes are active now?',
    questionHi: 'वर्तमान में वाहन सुख एवं यात्रा साधनों में क्या ग्रह-प्रभाव सक्रिय हैं?',
    shortLabelEn: 'Conveyance Themes Now',
    shortLabelHi: 'वर्तमान वाहन प्रभाव',
    intent: 'CURRENT_STATE',
    iconName: 'Target',
    keywords: ['vehicle themes active', 'conveyance status'],
    karmicElasticity: 'adridha',
    requiredFactors: ['4th House', 'Venus Gochar'],
    optionalFactors: ['Moon'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[59], k, l, o)
  },
  {
    id: 'q_vehicle_transportation_changes',
    domainId: 'vehicle',
    sphereId: 'artha',
    canonicalIntent: 'VEHICLE_TRANSPORTATION_CHANGES',
    categoryId: 'transitions_pivots',
    categoryNameEn: 'Transportation Upgrade',
    categoryNameHi: 'वाहन अपग्रेड व बदलाव',
    questionEn: 'When are transportation changes indicated?',
    questionHi: 'वाहन अपग्रेड, पुराने वाहन का विक्रय अथवा साधन बदलने का योग कब है?',
    shortLabelEn: 'Transportation Upgrade',
    shortLabelHi: 'वाहन अपग्रेड समय',
    intent: 'TRANSITION',
    iconName: 'Clock',
    keywords: ['transportation change', 'upgrade vehicle', 'car exchange'],
    karmicElasticity: 'adridha',
    requiredFactors: ['4th House', 'Venus', 'Mars'],
    optionalFactors: ['Mercury'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[60], k, l, o)
  },
  {
    id: 'q_vehicle_relocation_connection',
    domainId: 'vehicle',
    sphereId: 'artha',
    canonicalIntent: 'VEHICLE_RELOCATION_CONNECTION',
    categoryId: 'property_vehicles',
    categoryNameEn: 'Relocation & Travel Link',
    categoryNameHi: 'स्थान परिवर्तन व वाहन',
    questionEn: 'Are vehicle and relocation themes connected?',
    questionHi: 'क्या नया वाहन अथवा परिवहन साधन का संबंध निवास/शहर बदलाव से जुड़ा है?',
    shortLabelEn: 'Travel & Relocation Link',
    shortLabelHi: 'वाहन व स्थान परिवर्तन',
    intent: 'NATURE',
    iconName: 'Compass',
    keywords: ['vehicle relocation link', 'travel mode shift'],
    karmicElasticity: 'adridha',
    requiredFactors: ['4th House', '3rd House', 'Moon'],
    optionalFactors: ['Venus'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[61], k, l, o)
  },
  {
    id: 'q_vehicle_future_periods',
    domainId: 'vehicle',
    sphereId: 'artha',
    canonicalIntent: 'VEHICLE_FUTURE_PERIODS',
    categoryId: 'timing_windows',
    categoryNameEn: 'Future Conveyance Periods',
    categoryNameHi: 'भावी वाहन सुख काल',
    questionEn: 'What major conveyance periods lie ahead?',
    questionHi: 'भविष्य में वाहन सुख एवं श्रेष्ठ साधनों की प्राप्ति के प्रमुख काल-खंड कौन से हैं?',
    shortLabelEn: 'Future Conveyance Periods',
    shortLabelHi: 'भावी वाहन काल',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['future conveyance periods', 'luxury car timing'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['Venus Dasha', '4th House'],
    optionalFactors: ['Moon'],
    timingRequired: true,
    currentStateSupported: false,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[62], k, l, o)
  },

  // ==========================================================================
  // 9. HEALTH & VITALITY (health_vitality) — 5 Curated Questions (Responsible)
  // ==========================================================================
  {
    id: 'q_health_vitality_active_themes',
    domainId: 'health_vitality',
    sphereId: 'dharma',
    canonicalIntent: 'HEALTH_VITALITY_ACTIVE_THEMES',
    categoryId: 'current_phase',
    categoryNameEn: 'Active Vitality Themes',
    categoryNameHi: 'सक्रिय आरोग्य प्रभाव',
    questionEn: 'What vitality themes are currently active?',
    questionHi: 'वर्तमान में शारीरिक ऊर्जा, स्टेमिना एवं जीवन शक्ति में क्या ग्रह प्रभाव सक्रिय हैं?',
    shortLabelEn: 'Vitality Themes Now',
    shortLabelHi: 'वर्तमान आरोग्य प्रभाव',
    intent: 'CURRENT_STATE',
    iconName: 'Shield',
    keywords: ['vitality themes active', 'energy levels today', 'stamina'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['1st House (Lagna)', 'Sun', 'Mars'],
    optionalFactors: ['Moon'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[63], k, l, o)
  },
  {
    id: 'q_health_wellbeing_cycle',
    domainId: 'health_vitality',
    sphereId: 'dharma',
    canonicalIntent: 'HEALTH_WELLBEING_CYCLE',
    categoryId: 'health_wellbeing',
    categoryNameEn: 'Wellbeing Cycle',
    categoryNameHi: 'आरोग्य चक्र',
    questionEn: 'What is my current wellbeing cycle astrologically?',
    questionHi: 'वर्तमान में आरोग्य, शारीरिक ऊर्जा एवं जीवनशैली के कौन से ग्रह-प्रभाव सक्रिय हैं?',
    shortLabelEn: 'Wellbeing Cycle',
    shortLabelHi: 'आरोग्य व जीवनशैली चक्र',
    intent: 'CURRENT_STATE',
    iconName: 'Shield',
    keywords: ['wellbeing cycle', 'health vitality', 'ayurvedic lifestyle'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['1st House', 'Lagna Lord', 'Sun', '6th House'],
    optionalFactors: ['D-6'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[64], k, l, o)
  },
  {
    id: 'q_health_caution_wellbeing_periods',
    domainId: 'health_vitality',
    sphereId: 'dharma',
    canonicalIntent: 'HEALTH_CAUTION_WELLBEING_PERIODS',
    categoryId: 'challenges_mitigation',
    categoryNameEn: 'Rest & Caution Periods',
    categoryNameHi: 'विश्राम व सजगता काल',
    questionEn: 'When are periods requiring greater attention to wellbeing?',
    questionHi: 'किन काल-खंडों में पर्याप्त विश्राम, संतुलित खान-पान एवं दिनचर्या की विशेष आवश्यकता है?',
    shortLabelEn: 'Rest & Caution Periods',
    shortLabelHi: 'विश्राम व सजगता काल',
    intent: 'CHALLENGE',
    iconName: 'Clock',
    keywords: ['rest periods', 'caution wellbeing', 'lifestyle attention'],
    karmicElasticity: 'dridha',
    requiredFactors: ['6th House', '8th House', 'Saturn', 'Rahu'],
    optionalFactors: ['Moon'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[65], k, l, o)
  },
  {
    id: 'q_health_vitality_support_factors',
    domainId: 'health_vitality',
    sphereId: 'dharma',
    canonicalIntent: 'HEALTH_VITALITY_SUPPORT_FACTORS',
    categoryId: 'stability_continuity',
    categoryNameEn: 'Vitality Support Factors',
    categoryNameHi: 'आरोग्य प्रदायक ग्रह',
    questionEn: 'What supports vitality and balance according to the chart?',
    questionHi: 'कुंडली के अनुसार कौन से ग्रह व आयुर्वेदिक जीवनशैली तत्व समग्र आरोग्य को सुदृढ़ करते हैं?',
    shortLabelEn: 'Vitality Support Factors',
    shortLabelHi: 'आरोग्य प्रदायक तत्व',
    intent: 'STABILITY',
    iconName: 'Shield',
    keywords: ['vitality support', 'ayurvedic balance', 'wellness factors'],
    karmicElasticity: 'adridha',
    requiredFactors: ['Lagna Lord', 'Sun', 'Jupiter'],
    optionalFactors: ['Mars'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[66], k, l, o)
  },
  {
    id: 'q_health_future_themes',
    domainId: 'health_vitality',
    sphereId: 'dharma',
    canonicalIntent: 'HEALTH_FUTURE_THEMES',
    categoryId: 'timing_windows',
    categoryNameEn: 'Future Wellness Trends',
    categoryNameHi: 'भावी आरोग्य रुझान',
    questionEn: 'What wellbeing themes lie ahead?',
    questionHi: 'आगामी दशा चक्रों में जीवनशैली संतुलन एवं ऊर्जा संरक्षण के क्या रुझान हैं?',
    shortLabelEn: 'Future Wellness Trends',
    shortLabelHi: 'भावी आरोग्य रुझान',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['future wellness themes', 'long term vitality'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['Vimshottari Dasha', 'Sun', 'Saturn Transits'],
    optionalFactors: ['1st House'],
    timingRequired: true,
    currentStateSupported: false,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[67], k, l, o)
  },

  // ==========================================================================
  // 10. FOREIGN PR & TRAVEL (foreign_travel) — 7 Curated Questions
  // ==========================================================================
  {
    id: 'q_foreign_current_phase',
    domainId: 'foreign_travel',
    sphereId: 'artha',
    canonicalIntent: 'FOREIGN_CURRENT_PHASE',
    categoryId: 'current_phase',
    categoryNameEn: 'Current Foreign Travel Phase',
    categoryNameHi: 'वर्तमान विदेश गमन चरण',
    questionEn: 'What is my current foreign-travel/relocation phase?',
    questionHi: 'वर्तमान में विदेश प्रवास, यात्रा अथवा अंतर्राष्ट्रीय संपर्कों में क्या प्रभाव सक्रिय हैं?',
    shortLabelEn: 'Foreign Travel Phase Now',
    shortLabelHi: 'वर्तमान विदेश प्रवास स्थिति',
    intent: 'CURRENT_STATE',
    iconName: 'Plane',
    keywords: ['foreign travel now', 'current international phase'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['9th House', '12th House', 'Rahu', 'Moon'],
    optionalFactors: ['Current Dasha'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[68], k, l, o)
  },
  {
    id: 'q_foreign_travel_timing',
    domainId: 'foreign_travel',
    sphereId: 'artha',
    canonicalIntent: 'FOREIGN_TRAVEL_TIMING',
    categoryId: 'timing_windows',
    categoryNameEn: 'Foreign Travel Timing',
    categoryNameHi: 'विदेश यात्रा काल',
    questionEn: 'When is a good time for foreign travel?',
    questionHi: 'अंतर्राष्ट्रीय यात्रा, पर्यटन अथवा लघु प्रवास हेतु अनुकूल समय क्या है?',
    shortLabelEn: 'Foreign Travel Timing',
    shortLabelHi: 'विदेश यात्रा का समय',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['foreign travel timing', 'international trip date', 'videsh yatra timing'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['9th House', '3rd House', 'Moon', 'Venus'],
    optionalFactors: ['Rahu'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[69], k, l, o)
  },
  {
    id: 'q_foreign_long_stays',
    domainId: 'foreign_travel',
    sphereId: 'artha',
    canonicalIntent: 'FOREIGN_LONG_STAYS',
    categoryId: 'travel_foreign',
    categoryNameEn: 'Long-Term Foreign Stays',
    categoryNameHi: 'दीर्घकालीन विदेश वास',
    questionEn: 'When are longer foreign stays indicated?',
    questionHi: 'विदेश में दीर्घकालीन निवास अथवा कार्य प्रवास के प्रबल अवसर कब हैं?',
    shortLabelEn: 'Long-Term Foreign Stays',
    shortLabelHi: 'दीर्घकालीन विदेश वास',
    intent: 'TIMING',
    iconName: 'Plane',
    keywords: ['long foreign stays', 'working abroad timing', 'extended overseas stay'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['12th House', '9th House', 'Rahu', 'Saturn'],
    optionalFactors: ['D-9'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[70], k, l, o)
  },
  {
    id: 'q_foreign_residence_pr_support',
    domainId: 'foreign_travel',
    sphereId: 'artha',
    canonicalIntent: 'FOREIGN_RESIDENCE_PR_SUPPORT',
    categoryId: 'travel_foreign',
    categoryNameEn: 'Permanent Residency (PR)',
    categoryNameHi: 'विदेश में स्थायी निवास (पीआर)',
    questionEn: 'Is foreign residence / permanent residency (PR) supported?',
    questionHi: 'क्या कुंडली में विदेश गमन, स्थायी निवास (पीआर) एवं अंतर्राष्ट्रीय प्रवास के योग हैं?',
    shortLabelEn: 'Foreign PR & Settlement',
    shortLabelHi: 'विदेश पीआर व स्थायी निवास',
    intent: 'POSSIBILITY',
    iconName: 'Plane',
    keywords: ['foreign PR', 'settle abroad', 'permanent residency kundali'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['9th House', '12th House', 'Rahu', 'Moon', 'D-9'],
    optionalFactors: ['D-12'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[71], k, l, o)
  },
  {
    id: 'q_foreign_relocation_activation',
    domainId: 'foreign_travel',
    sphereId: 'artha',
    canonicalIntent: 'FOREIGN_RELOCATION_ACTIVATION',
    categoryId: 'transitions_pivots',
    categoryNameEn: 'Relocation Abroad Window',
    categoryNameHi: 'विदेश स्थानांतरण काल',
    questionEn: 'When is relocation abroad activated?',
    questionHi: 'वीजा स्वीकृति एवं विदेश स्थानांतरण की सक्रिय समय-सीमा क्या है?',
    shortLabelEn: 'Relocation Abroad Window',
    shortLabelHi: 'विदेश स्थानांतरण समय',
    intent: 'TIMING',
    iconName: 'Compass',
    keywords: ['visa approval timing', 'relocation abroad date', 'overseas shift'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['12th House', 'Rahu Gochar', 'Operating Dasha'],
    optionalFactors: ['Jupiter'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[72], k, l, o)
  },
  {
    id: 'q_foreign_career_education_connections',
    domainId: 'foreign_travel',
    sphereId: 'artha',
    canonicalIntent: 'FOREIGN_CAREER_EDUCATION_CONNECTIONS',
    categoryId: 'travel_foreign',
    categoryNameEn: 'Global Career & Study Links',
    categoryNameHi: 'वैश्विक करियर व शिक्षा संबंध',
    questionEn: 'What professional or educational foreign connections are indicated?',
    questionHi: 'बहुराष्ट्रीय कंपनियों, विदेशी ग्राहकों अथवा अंतर्राष्ट्रीय प्रोजेक्ट्स से क्या लाभ योग हैं?',
    shortLabelEn: 'Global Career Links',
    shortLabelHi: 'वैश्विक करियर संबंध',
    intent: 'NATURE',
    iconName: 'Briefcase',
    keywords: ['global career', 'mnc employment', 'cross border projects'],
    karmicElasticity: 'adridha',
    requiredFactors: ['10th House', '9th House', '12th House', 'Mercury'],
    optionalFactors: ['Rahu'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[73], k, l, o)
  },
  {
    id: 'q_foreign_future_periods',
    domainId: 'foreign_travel',
    sphereId: 'artha',
    canonicalIntent: 'FOREIGN_FUTURE_PERIODS',
    categoryId: 'timing_windows',
    categoryNameEn: 'Future International Travel',
    categoryNameHi: 'भावी अंतर्राष्ट्रीय यात्रा काल',
    questionEn: 'What major foreign-travel periods lie ahead?',
    questionHi: 'भविष्य में अंतर्राष्ट्रीय यात्राओं एवं विदेशी दौरों के प्रमुख काल-खंड कब हैं?',
    shortLabelEn: 'Future Travel Periods',
    shortLabelHi: 'भावी विदेश यात्रा काल',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['future travel periods', 'overseas trip timeline'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['9th/12th House Dashas', 'Rahu Cycle'],
    optionalFactors: ['Moon'],
    timingRequired: true,
    currentStateSupported: false,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[74], k, l, o)
  },

  // ==========================================================================
  // 11. PROTECTION & COURT CASES (litigation_competition) — 6 Curated Questions
  // ==========================================================================
  {
    id: 'q_legal_active_themes',
    domainId: 'litigation_competition',
    sphereId: 'artha',
    canonicalIntent: 'LEGAL_ACTIVE_THEMES',
    categoryId: 'legal_protection',
    categoryNameEn: 'Active Legal Themes',
    categoryNameHi: 'सक्रिय विधिक स्थिति',
    questionEn: 'Are legal or dispute-related themes currently active?',
    questionHi: 'क्या वर्तमान में कानूनी, विधिक अथवा विवाद संबंधी प्रभाव सक्रिय हैं?',
    shortLabelEn: 'Legal Themes Now',
    shortLabelHi: 'वर्तमान विधिक स्थिति',
    intent: 'CURRENT_STATE',
    iconName: 'Scale',
    keywords: ['legal themes active', 'dispute status now', 'court case today'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['6th House', '6th Lord', 'Mars', 'Saturn'],
    optionalFactors: ['Current Dasha'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[75], k, l, o)
  },
  {
    id: 'q_legal_attention_periods',
    domainId: 'litigation_competition',
    sphereId: 'artha',
    canonicalIntent: 'LEGAL_ATTENTION_PERIODS',
    categoryId: 'timing_windows',
    categoryNameEn: 'Procedural Vigilance Windows',
    categoryNameHi: 'सतर्कता एवं प्रक्रियात्मक काल',
    questionEn: 'When are periods of increased legal attention indicated?',
    questionHi: 'किन काल-खंडों में कानूनी व प्रक्रियात्मक सतर्कता की विशेष आवश्यकता है?',
    shortLabelEn: 'Procedural Vigilance Windows',
    shortLabelHi: 'सतर्कता काल',
    intent: 'CHALLENGE',
    iconName: 'Clock',
    keywords: ['legal attention periods', 'litigation timing', 'compliance caution'],
    popularChip: true,
    karmicElasticity: 'dridha',
    requiredFactors: ['6th House', '8th House', 'Mars', 'Rahu'],
    optionalFactors: ['Saturn'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[76], k, l, o)
  },
  {
    id: 'q_legal_protection_themes',
    domainId: 'litigation_competition',
    sphereId: 'artha',
    canonicalIntent: 'LEGAL_PROTECTION_THEMES',
    categoryId: 'legal_protection',
    categoryNameEn: 'Shatru Hanta & Protection',
    categoryNameHi: 'शत्रुहंता व रक्षा योग',
    questionEn: 'What protection-related themes are active?',
    questionHi: 'कुंडली में विरोधियों पर नियंत्रण एवं सुरक्षा प्रदायक कौन से योग सक्रिय हैं?',
    shortLabelEn: 'Protection & Defense Yogas',
    shortLabelHi: 'शत्रुहंता व रक्षा योग',
    intent: 'STABILITY',
    iconName: 'Shield',
    keywords: ['protection themes', 'shatru hanta', 'defense yogas'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['6th House in Upachaya', 'Mars', 'Jupiter'],
    optionalFactors: ['Sun'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[77], k, l, o)
  },
  {
    id: 'q_legal_transitions_timing',
    domainId: 'litigation_competition',
    sphereId: 'artha',
    canonicalIntent: 'LEGAL_TRANSITIONS_TIMING',
    categoryId: 'transitions_pivots',
    categoryNameEn: 'Dispute Resolution Windows',
    categoryNameHi: 'विवाद समाधान काल',
    questionEn: 'When are major legal transitions indicated?',
    questionHi: 'विवादों के शांतिपूर्ण समाधान, समझौते अथवा निर्णय के अनुकूल अवसर कब हैं?',
    shortLabelEn: 'Dispute Resolution Windows',
    shortLabelHi: 'समझौता व निर्णय काल',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['dispute resolution', 'court settlement timing', 'legal outcome date'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['6th House', 'Jupiter Aspect', 'Dasha Sub-Period'],
    optionalFactors: ['Mars'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[78], k, l, o)
  },
  {
    id: 'q_legal_cycle_status',
    domainId: 'litigation_competition',
    sphereId: 'artha',
    canonicalIntent: 'LEGAL_CYCLE_STATUS',
    categoryId: 'current_phase',
    categoryNameEn: 'Legal Cycle Status',
    categoryNameHi: 'विधिक चक्र स्थिति',
    questionEn: 'What is the current legal-cycle status?',
    questionHi: 'वर्तमान में विधिक प्रक्रियाओं का क्या चरण व दिशा चल रही है?',
    shortLabelEn: 'Legal Cycle Status',
    shortLabelHi: 'विधिक चक्र स्थिति',
    intent: 'CURRENT_STATE',
    iconName: 'Target',
    keywords: ['legal cycle status', 'litigation phase'],
    karmicElasticity: 'adridha',
    requiredFactors: ['Current Dasha', '6th Lord Gochar'],
    optionalFactors: ['Saturn'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[79], k, l, o)
  },
  {
    id: 'q_legal_practical_attention',
    domainId: 'litigation_competition',
    sphereId: 'artha',
    canonicalIntent: 'LEGAL_PRACTICAL_ATTENTION',
    categoryId: 'challenges_mitigation',
    categoryNameEn: 'Practical Safeguards',
    categoryNameHi: 'व्यावहारिक सुरक्षा उपाय',
    questionEn: 'What periods require greater practical attention?',
    questionHi: 'दस्तावेजीकरण एवं कानूनी समझौतों में किन समय-खंडों में विशेष सावधानी बरतनी चाहिए?',
    shortLabelEn: 'Practical Safeguards',
    shortLabelHi: 'व्यावहारिक सावधानी',
    intent: 'CHALLENGE',
    iconName: 'Shield',
    keywords: ['practical safeguards', 'documentation caution', 'contract review'],
    karmicElasticity: 'dridha',
    requiredFactors: ['Mercury (Contracts)', '8th House', 'Rahu'],
    optionalFactors: ['Mars'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[80], k, l, o)
  },

  // ==========================================================================
  // 12. GURU & SELF-REALIZATION (spirituality) — 7 Curated Questions
  // ==========================================================================
  {
    id: 'q_spirituality_active_themes',
    domainId: 'spirituality',
    sphereId: 'moksha',
    canonicalIntent: 'SPIRITUALITY_ACTIVE_THEMES',
    categoryId: 'current_phase',
    categoryNameEn: 'Active Spiritual Themes',
    categoryNameHi: 'सक्रिय आध्यात्मिक प्रभाव',
    questionEn: 'What spiritual themes are currently active?',
    questionHi: 'वर्तमान में साधना, ध्यान, गुरु कृपा एवं आत्म-विकास के कौन से आध्यात्मिक योग सक्रिय हैं?',
    shortLabelEn: 'Spiritual Themes Now',
    shortLabelHi: 'वर्तमान साधना स्थिति',
    intent: 'CURRENT_STATE',
    iconName: 'Sparkles',
    keywords: ['spiritual themes active', 'meditation phase', 'sadhana today'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['9th House', '12th House', 'Jupiter', 'Ketu', 'D-20'],
    optionalFactors: ['Sun'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[81], k, l, o)
  },
  {
    id: 'q_spirituality_growth_periods',
    domainId: 'spirituality',
    sphereId: 'moksha',
    canonicalIntent: 'SPIRITUALITY_GROWTH_PERIODS',
    categoryId: 'timing_windows',
    categoryNameEn: 'Spiritual Awakening Windows',
    categoryNameHi: 'आत्म-जागरण काल',
    questionEn: 'When are stronger periods for spiritual development?',
    questionHi: 'गहन साधना, मंत्र सिद्धि एवं आत्म-विकास के श्रेष्ठ काल-खंड कब हैं?',
    shortLabelEn: 'Spiritual Awakening Windows',
    shortLabelHi: 'आत्म-विकास काल',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['spiritual growth timing', 'sadhana periods', 'inner awakening windows'],
    popularChip: true,
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['9th/12th House', 'Jupiter Transits', 'Ketu'],
    optionalFactors: ['D-20'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[82], k, l, o)
  },
  {
    id: 'q_spirituality_inner_phase',
    domainId: 'spirituality',
    sphereId: 'moksha',
    canonicalIntent: 'SPIRITUALITY_INNER_PHASE',
    categoryId: 'spiritual_growth',
    categoryNameEn: 'Inner Development Phase',
    categoryNameHi: 'आंतरिक चेतना चरण',
    questionEn: 'What is my current inner-development phase?',
    questionHi: 'वर्तमान में चेतना एवं आध्यात्मिक परिपक्वता का क्या स्तर सक्रिय है?',
    shortLabelEn: 'Inner Development Phase',
    shortLabelHi: 'आंतरिक चेतना चरण',
    intent: 'CURRENT_STATE',
    iconName: 'Target',
    keywords: ['inner development phase', 'spiritual maturity'],
    karmicElasticity: 'adridha',
    requiredFactors: ['Current Dasha', 'Moksha Trikona'],
    optionalFactors: ['Jupiter'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[83], k, l, o)
  },
  {
    id: 'q_spirituality_meditation_sadhana',
    domainId: 'spirituality',
    sphereId: 'moksha',
    canonicalIntent: 'SPIRITUALITY_MEDITATION_SADHANA',
    categoryId: 'spiritual_growth',
    categoryNameEn: 'Meditation & Sadhana',
    categoryNameHi: 'ध्यान व साधना योग',
    questionEn: 'When are meditation and self-development themes activated?',
    questionHi: 'ध्यान, मौन एवं अंतर्मुखी अभ्यास हेतु अनुकूल समय क्या है?',
    shortLabelEn: 'Meditation & Sadhana',
    shortLabelHi: 'ध्यान व साधना काल',
    intent: 'TIMING',
    iconName: 'Sparkles',
    keywords: ['meditation timing', 'sadhana retreat', 'samadhi yoga'],
    karmicElasticity: 'adridha',
    requiredFactors: ['12th House', '8th House', 'Ketu', 'Moon'],
    optionalFactors: ['Jupiter'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[84], k, l, o)
  },
  {
    id: 'q_spirituality_guru_guidance',
    domainId: 'spirituality',
    sphereId: 'moksha',
    canonicalIntent: 'SPIRITUALITY_GURU_GUIDANCE',
    categoryId: 'spiritual_growth',
    categoryNameEn: 'Guru & Mentorship',
    categoryNameHi: 'गुरु कृपा व दीक्षा',
    questionEn: 'What Guru/guidance themes are indicated?',
    questionHi: 'सद्गुरु मिलन, दीक्षा प्राप्ति अथवा उच्च आध्यात्मिक मार्गदर्शन के क्या योग हैं?',
    shortLabelEn: 'Guru & Mentorship',
    shortLabelHi: 'गुरु कृपा व दीक्षा',
    intent: 'POSSIBILITY',
    iconName: 'Sparkles',
    keywords: ['guru diksha', 'spiritual master', 'guru guidance in chart'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['9th House', 'Jupiter', 'Sun', 'D-20'],
    optionalFactors: ['5th House'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[85], k, l, o)
  },
  {
    id: 'q_spirituality_introspection_periods',
    domainId: 'spirituality',
    sphereId: 'moksha',
    canonicalIntent: 'SPIRITUALITY_INTROSPECTION_PERIODS',
    categoryId: 'challenges_mitigation',
    categoryNameEn: 'Introspection Periods',
    categoryNameHi: 'आत्म-मंथन काल',
    questionEn: 'What periods support deeper introspection?',
    questionHi: 'किन काल-खंडों में सांसारिक आपाधापी से अलग होकर आत्म-मंथन करना सर्वाधिक फलदायी रहता है?',
    shortLabelEn: 'Introspection Periods',
    shortLabelHi: 'आत्म-मंथन काल',
    intent: 'DEVELOPMENT',
    iconName: 'Clock',
    keywords: ['introspection periods', 'spiritual retreat timing', 'vairagya'],
    karmicElasticity: 'adridha',
    requiredFactors: ['12th House', 'Saturn', 'Ketu'],
    optionalFactors: ['Moon'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[86], k, l, o)
  },
  {
    id: 'q_spirituality_future_themes',
    domainId: 'spirituality',
    sphereId: 'moksha',
    canonicalIntent: 'SPIRITUALITY_FUTURE_THEMES',
    categoryId: 'timing_windows',
    categoryNameEn: 'Future Spiritual Milestones',
    categoryNameHi: 'भावी आध्यात्मिक मील के पत्थर',
    questionEn: 'What spiritual themes lie ahead?',
    questionHi: 'भविष्य में तीर्थाटन, धर्म कार्य एवं उच्च चेतना विकास के प्रमुख पड़ाव कौन से हैं?',
    shortLabelEn: 'Future Spiritual Milestones',
    shortLabelHi: 'भावी आध्यात्मिक काल',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['future spiritual milestones', 'pilgrimage timing', 'tirtha yatra'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['9th/12th House Dashas', 'Jupiter Returns'],
    optionalFactors: ['Ketu'],
    timingRequired: true,
    currentStateSupported: false,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[87], k, l, o)
  },

  // ==========================================================================
  // 13. SIBLINGS & FAMILY BOND (family_parents) — 7 Curated Questions
  // ==========================================================================
  {
    id: 'q_family_active_themes',
    domainId: 'family_parents',
    sphereId: 'dharma',
    canonicalIntent: 'FAMILY_ACTIVE_THEMES',
    categoryId: 'current_phase',
    categoryNameEn: 'Active Family Themes',
    categoryNameHi: 'सक्रिय पारिवारिक स्थिति',
    questionEn: 'What family themes are currently active?',
    questionHi: 'वर्तमान में पारिवारिक सौहार्द, कुल दायित्व एवं भाई-बहनों के संबंधों में क्या ग्रह-प्रभाव सक्रिय हैं?',
    shortLabelEn: 'Family Themes Now',
    shortLabelHi: 'वर्तमान पारिवारिक प्रभाव',
    intent: 'CURRENT_STATE',
    iconName: 'Users',
    keywords: ['family themes active', 'current household phase'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['2nd House', '3rd House', '4th House', 'Moon'],
    optionalFactors: ['Jupiter'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[88], k, l, o)
  },
  {
    id: 'q_family_sibling_relations',
    domainId: 'family_parents',
    sphereId: 'dharma',
    canonicalIntent: 'FAMILY_SIBLING_RELATIONS',
    categoryId: 'family_dynamics',
    categoryNameEn: 'Sibling Relations',
    categoryNameHi: 'सहोदर संबंध',
    questionEn: 'What sibling relationship themes are indicated?',
    questionHi: 'सहोदर भाई-बहनों के साथ सहयोग, प्रेम एवं संवाद में क्या ग्रह-स्थितियां दिखाई देती हैं?',
    shortLabelEn: 'Sibling Relations',
    shortLabelHi: 'सहोदर संबंध',
    intent: 'PARTNER_DYNAMICS',
    iconName: 'Users',
    keywords: ['sibling relations', 'brothers sisters bond', 'bhrata sukha'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['3rd House', 'Mars', 'D-3 Drekkana'],
    optionalFactors: ['Mercury'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[89], k, l, o)
  },
  {
    id: 'q_family_important_periods',
    domainId: 'family_parents',
    sphereId: 'dharma',
    canonicalIntent: 'FAMILY_IMPORTANT_PERIODS',
    categoryId: 'timing_windows',
    categoryNameEn: 'Family Celebration Windows',
    categoryNameHi: 'मांगलिक पारिवारिक काल',
    questionEn: 'When are important family periods?',
    questionHi: 'कुल में मांगलिक आयोजनों, पारिवारिक उत्सवों एवं मिलन के प्रमुख काल-खंड कब हैं?',
    shortLabelEn: 'Family Celebration Windows',
    shortLabelHi: 'मांगलिक पारिवारिक काल',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['family celebration periods', 'family gatherings', 'auspicious family events'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['2nd House', '4th House', 'Jupiter'],
    optionalFactors: ['Venus'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[90], k, l, o)
  },
  {
    id: 'q_family_responsibilities_growth',
    domainId: 'family_parents',
    sphereId: 'dharma',
    canonicalIntent: 'FAMILY_RESPONSIBILITIES_GROWTH',
    categoryId: 'family_dynamics',
    categoryNameEn: 'Kinship Responsibilities',
    categoryNameHi: 'पारिवारिक उत्तरदायित्व',
    questionEn: 'Are family responsibilities increasing?',
    questionHi: 'क्या माता-पिता व कुल के प्रति दायित्वों में वृद्धि हो रही है, और इसका निर्वहन कैसे करें?',
    shortLabelEn: 'Kinship Responsibilities',
    shortLabelHi: 'पारिवारिक दायित्व',
    intent: 'DEVELOPMENT',
    iconName: 'Shield',
    keywords: ['family responsibilities', 'parental care', 'kinship duties'],
    karmicElasticity: 'adridha',
    requiredFactors: ['2nd House', '9th House', 'Saturn', 'Sun'],
    optionalFactors: ['Moon'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[91], k, l, o)
  },
  {
    id: 'q_family_harmony_factors',
    domainId: 'family_parents',
    sphereId: 'dharma',
    canonicalIntent: 'FAMILY_HARMONY_FACTORS',
    categoryId: 'stability_continuity',
    categoryNameEn: 'Family Harmony',
    categoryNameHi: 'पारिवारिक सौहार्द',
    questionEn: 'What supports family harmony?',
    questionHi: 'घर में शांति, समन्वय एवं आपसी विश्वास बनाए रखने में कौन से ग्रह व आचरण सहायक हैं?',
    shortLabelEn: 'Family Harmony Factors',
    shortLabelHi: 'पारिवारिक सौहार्द',
    intent: 'STABILITY',
    iconName: 'Heart',
    keywords: ['family harmony', 'domestic peace', 'parivarik shanti'],
    popularChip: true,
    karmicElasticity: 'adridha',
    requiredFactors: ['4th House', '2nd House', 'Moon', 'Jupiter'],
    optionalFactors: ['Venus'],
    timingRequired: false,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[92], k, l, o)
  },
  {
    id: 'q_family_transitions_timing',
    domainId: 'family_parents',
    sphereId: 'dharma',
    canonicalIntent: 'FAMILY_TRANSITIONS_TIMING',
    categoryId: 'transitions_pivots',
    categoryNameEn: 'Family Transitions',
    categoryNameHi: 'पारिवारिक रूपांतरण',
    questionEn: 'When are major family transitions indicated?',
    questionHi: 'पारिवारिक संरचना अथवा निवास में महत्वपूर्ण बदलाव के योग कब हैं?',
    shortLabelEn: 'Family Transitions',
    shortLabelHi: 'पारिवारिक बदलाव समय',
    intent: 'TRANSITION',
    iconName: 'Clock',
    keywords: ['family transitions', 'household shift'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['4th/12th House', 'Saturn/Rahu Transit'],
    optionalFactors: ['Moon'],
    timingRequired: true,
    currentStateSupported: true,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[93], k, l, o)
  },
  {
    id: 'q_family_future_themes',
    domainId: 'family_parents',
    sphereId: 'dharma',
    canonicalIntent: 'FAMILY_FUTURE_THEMES',
    categoryId: 'timing_windows',
    categoryNameEn: 'Future Family Milestones',
    categoryNameHi: 'भावी पारिवारिक काल',
    questionEn: 'What family themes lie ahead?',
    questionHi: 'भविष्य में कुल उन्नति एवं पारिवारिक सौहार्द के प्रमुख काल-खंड कौन से हैं?',
    shortLabelEn: 'Future Family Themes',
    shortLabelHi: 'भावी पारिवारिक काल',
    intent: 'TIMING',
    iconName: 'Calendar',
    keywords: ['future family themes', 'household timeline'],
    karmicElasticity: 'dridha_adridha',
    requiredFactors: ['2nd/4th House Dashas', 'Jupiter Transits'],
    optionalFactors: ['Moon'],
    timingRequired: true,
    currentStateSupported: false,
    evaluate: (k, l, o) => buildCanonicalAnswer(CANONICAL_QUESTIONS_REGISTRY[94], k, l, o)
  }
];
